import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook for controlling spectate mode playback speed
 * Queues incoming game state updates and processes them at a controlled rate
 * 
 * @param {boolean} enabled - Whether speed control is enabled (only in spectate mode)
 * @param {number} initialSpeed - Initial speed percentage (1-100, default 100)
 * @param {WebSocket} socket - WebSocket connection for sending pause/resume to backend
 * @param {string} gameId - Game ID for pause/resume messages
 * @returns {object} Speed control state and functions
 */
export function useSpectateSpeedControl(enabled = false, initialSpeed = 100, socket = null, gameId = null) {
  const [speed, setSpeed] = useState(initialSpeed);
  const [isPaused, setIsPaused] = useState(false);
  const [backendPaused, setBackendPaused] = useState(false); // Track if backend has confirmed pause
  
  // Queue for game state updates
  const updateQueueRef = useRef([]);
  const processingRef = useRef(false);
  const intervalRef = useRef(null);
  const isPausedRef = useRef(false); // Ref to track pause state for interval callback
  const timeoutIdsRef = useRef([]); // Track all setTimeout IDs so we can clear them on pause
  
  // Calculate delay based on speed (1% = 100x slower, 100% = real-time)
  // At 100% speed, process immediately (0ms delay)
  // At 1% speed, delay ~10 seconds between updates (100x slower)
  // Formula: delay = (100 / speedPercent - 1) * baseDelay
  // This gives us: 100% = 0ms, 50% = 100ms (2x slower), 1% = 9900ms (100x slower)
  const getDelay = useCallback((speedPercent) => {
    if (speedPercent >= 100) return 0; // Real-time - no delay
    if (speedPercent <= 0) return 10000; // Very slow fallback
    
    // Base delay represents typical time between updates (e.g., 100ms)
    // At 50% speed: (100/50 - 1) * 100 = 100ms (2x slower)
    // At 1% speed: (100/1 - 1) * 100 = 9900ms (100x slower)
    const baseDelay = 100; // Typical time between game state updates
    const delay = (100 / speedPercent - 1) * baseDelay;
    
    // Ensure minimum delay of 10ms to prevent too-fast processing
    return Math.max(10, Math.round(delay));
  }, []);
  
  /**
   * Process all queued updates immediately (for real-time speed)
   * Uses setTimeout(0) to allow pause to interrupt processing
   */
  const processAllQueued = useCallback(() => {
    // Don't process if paused
    if (isPausedRef.current) {
      console.log('⏸️ [Speed Control] processAllQueued: Paused - not processing');
      processingRef.current = false;
      return;
    }
    
    // Process updates one at a time with setTimeout to allow pause to interrupt
    const processNext = () => {
      // CRITICAL: Check pause FIRST before doing anything
      if (isPausedRef.current) {
        console.log('⏸️ [Speed Control] processNext: Pause detected - stopping immediately');
        processingRef.current = false;
        return;
      }
      
      if (updateQueueRef.current.length === 0) {
        // Queue empty - done processing
        console.log('▶️ [Speed Control] processAllQueued: Queue empty - done processing');
        processingRef.current = false;
        return;
      }
      
      // CRITICAL: Check pause again right before taking from queue
      if (isPausedRef.current) {
        console.log('⏸️ [Speed Control] processNext: Pause detected before taking from queue - stopping');
        processingRef.current = false;
        return;
      }
      
      const next = updateQueueRef.current.shift();
      const remainingCount = updateQueueRef.current.length;
      console.log(`▶️ [Speed Control] processNext: Processing update (${remainingCount} remaining)`);
      
      // CRITICAL: Final pause check right before applying (defense in depth)
      if (isPausedRef.current) {
        console.log('⏸️ [Speed Control] processNext: Pause detected right before applyUpdate - re-queuing and stopping');
        // Re-queue the update so it can be processed on resume
        updateQueueRef.current.unshift(next);
        processingRef.current = false;
        return;
      }
      
      // Apply the update (this might be slow - React re-renders)
      const startTime = performance.now();
      try {
        next.applyUpdate();
      } catch (error) {
        console.error('❌ [Speed Control] Error in applyUpdate:', error);
      }
      const duration = performance.now() - startTime;
      if (duration > 10) {
        console.warn(`⚠️ [Speed Control] applyUpdate took ${duration.toFixed(2)}ms - this might delay pause`);
      }
      
      // CRITICAL: Check pause again after applying (in case pause was clicked during applyUpdate)
      if (isPausedRef.current) {
        console.log('⏸️ [Speed Control] processNext: Pause detected after applyUpdate - stopping');
        processingRef.current = false;
        return;
      }
      
      // Process next update asynchronously to allow pause to interrupt
      // Use a small delay even at 100% to allow pause to be checked
      const timeoutId = setTimeout(() => {
        // Remove from tracking array when executed
        const index = timeoutIdsRef.current.indexOf(timeoutId);
        if (index > -1) {
          timeoutIdsRef.current.splice(index, 1);
        }
        // Check pause one more time before recursing
        if (!isPausedRef.current) {
          processNext();
        } else {
          console.log('⏸️ [Speed Control] processNext: Pause detected in setTimeout callback - stopping');
          processingRef.current = false;
        }
      }, 0);
      
      // Track this timeout so we can clear it on pause
      timeoutIdsRef.current.push(timeoutId);
    };
    
    // Start processing
    console.log(`▶️ [Speed Control] processAllQueued: Starting (queue: ${updateQueueRef.current.length})`);
    processNext();
  }, []);
  
  /**
   * Start processing the queue at the current speed
   */
  const startProcessing = useCallback(() => {
    if (processingRef.current) {
      console.log('⏸️ [Speed Control] Already processing, skipping start');
      return;
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    // Don't start if paused
    if (isPausedRef.current) {
      console.log('⏸️ [Speed Control] Paused - not starting processing');
      return;
    }
    
    console.log(`▶️ [Speed Control] Starting processing at ${speed}% speed`);
    processingRef.current = true;
    const delay = getDelay(speed);
    
    if (delay === 0) {
      // Real-time: process all queued updates immediately (but check pause first and during)
      if (!isPausedRef.current) {
        console.log('▶️ [Speed Control] Processing all queued updates immediately (100% speed)');
        processAllQueued();
      } else {
        console.log('⏸️ [Speed Control] Paused - not processing queued updates');
        processingRef.current = false;
      }
    } else {
      // Process one update per interval
      console.log(`▶️ [Speed Control] Starting interval with ${delay}ms delay`);
      intervalRef.current = setInterval(() => {
        // Check pause state from ref (always current)
        if (isPausedRef.current) {
          // Paused - stop processing
          console.log('⏸️ [Speed Control] Pause detected in interval - stopping');
          processingRef.current = false;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }
        
        if (updateQueueRef.current.length > 0) {
          const next = updateQueueRef.current.shift();
          console.log(`▶️ [Speed Control] Processing queued update (${updateQueueRef.current.length} remaining)`);
          next.applyUpdate();
        } else if (updateQueueRef.current.length === 0) {
          // Queue empty - stop processing
          console.log('▶️ [Speed Control] Queue empty - stopping processing');
          processingRef.current = false;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, delay);
    }
  }, [speed, getDelay, processAllQueued]);
  
  /**
   * Queue a game state update for delayed processing
   * @param {object} message - WebSocket message with gameState
   * @param {function} applyUpdate - Function to apply the update
   */
  const queueUpdate = useCallback((message, applyUpdate) => {
    console.log(`📥 [Speed Control] queueUpdate called: type=${message?.type}, enabled=${enabled}, isPaused=${isPausedRef.current}, processing=${processingRef.current}`);
    
    if (!enabled) {
      // Speed control disabled - apply immediately
      console.log('  → Speed control disabled - applying immediately');
      applyUpdate();
      return;
    }
    
    // Always add to queue first
    updateQueueRef.current.push({
      message,
      applyUpdate,
      timestamp: Date.now()
    });
    
    console.log(`  → Update queued (queue length: ${updateQueueRef.current.length})`);
    
    // If paused, don't start processing - updates will queue up
    if (isPausedRef.current) {
      console.log(`⏸️ [Speed Control] Update queued while paused (queue length: ${updateQueueRef.current.length}) - will process on resume`);
      return; // Don't start processing while paused
    }
    
    // Start processing if not already running
    if (!processingRef.current) {
      console.log('  → Starting processing (not already running)');
      startProcessing();
    } else {
      console.log('  → Processing already running, update will be processed in order');
    }
  }, [enabled, startProcessing]);
  
  /**
   * Send pause/resume message to backend
   */
  const sendPauseToBackend = useCallback((shouldPause) => {
    if (!socket || !gameId) {
      console.log(`⚠️ [Speed Control] Cannot send pause to backend - no socket or gameId`, { hasSocket: !!socket, gameId });
      return false;
    }
    
    if (socket.readyState !== WebSocket.OPEN) {
      console.log(`⚠️ [Speed Control] Cannot send pause to backend - socket not open (state: ${socket.readyState})`);
      return false;
    }
    
    const messageType = shouldPause ? 'autobattler-pause' : 'autobattler-resume';
    console.log(`📤 [Speed Control] Sending ${messageType} to backend for game ${gameId}`);
    
    try {
      socket.send(JSON.stringify({
        type: messageType,
        gameId: gameId
      }));
      return true;
    } catch (error) {
      console.error(`❌ [Speed Control] Error sending ${messageType}:`, error);
      return false;
    }
  }, [socket, gameId]);

  /**
   * Send speed change to backend
   */
  const sendSpeedToBackend = useCallback((speedPercent) => {
    if (!socket || !gameId) {
      console.log(`⚠️ [Speed Control] Cannot send speed to backend - no socket or gameId`, { hasSocket: !!socket, gameId });
      return false;
    }
    
    if (socket.readyState !== WebSocket.OPEN) {
      console.log(`⚠️ [Speed Control] Cannot send speed to backend - socket not open (state: ${socket.readyState})`);
      return false;
    }
    
    console.log(`📤 [Speed Control] Sending autobattler-speed (${speedPercent}%) to backend for game ${gameId}`);
    
    try {
      socket.send(JSON.stringify({
        type: 'autobattler-speed',
        gameId: gameId,
        speed: speedPercent
      }));
      return true;
    } catch (error) {
      console.error(`❌ [Speed Control] Error sending speed change:`, error);
      return false;
    }
  }, [socket, gameId]);

  /**
   * Update speed and restart processing if needed
   */
  const updateSpeed = useCallback((newSpeed) => {
    const clampedSpeed = Math.max(1, Math.min(100, newSpeed));
    console.log(`⏩ [Speed Control] updateSpeed called: ${speed}% → ${clampedSpeed}%`);
    setSpeed(clampedSpeed);
    
    // ✅ CRITICAL: Send speed change to backend so it adjusts turn delay
    const sentToBackend = sendSpeedToBackend(clampedSpeed);
    console.log(`   📤 Sent to backend: ${sentToBackend}`);
    
    // Restart processing with new speed (only if not paused)
    if (processingRef.current && !isPausedRef.current) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      processingRef.current = false;
      startProcessing();
    }
  }, [speed, startProcessing, sendSpeedToBackend]);

  /**
   * Handle backend pause/resume confirmation
   */
  const handleBackendPauseConfirmation = useCallback((confirmed, isPausedState) => {
    console.log(`✅ [Speed Control] Backend confirmed pause state: ${isPausedState}`);
    setBackendPaused(isPausedState);
    
    if (!isPausedState) {
      // Backend resumed - start processing queued updates
      console.log('▶️ [Speed Control] Backend resumed - starting to process queue');
      console.log(`   Queue length: ${updateQueueRef.current.length}`);
      startProcessing();
    }
  }, [startProcessing]);

  /**
   * Toggle pause state
   */
  const togglePause = useCallback(() => {
    console.log('🔘 [Speed Control] togglePause CALLED');
    console.log(`   Current isPaused state: ${isPaused}`);
    console.log(`   Current isPausedRef: ${isPausedRef.current}`);
    console.log(`   Current processing: ${processingRef.current}`);
    console.log(`   Current queue length: ${updateQueueRef.current.length}`);
    console.log(`   Current interval: ${intervalRef.current ? 'exists' : 'null'}`);
    console.log(`   Pending timeouts: ${timeoutIdsRef.current.length}`);
    console.log(`   Has socket: ${!!socket}, Has gameId: ${!!gameId}`);
    
    const newPaused = !isPaused;
    
    // ✅ CRITICAL: Send pause/resume to backend FIRST (before updating local state)
    // This ensures the backend stops executing turns immediately
    const sentToBackend = sendPauseToBackend(newPaused);
    console.log(`   📤 Sent to backend: ${sentToBackend}`);
    
    // CRITICAL: Update ref IMMEDIATELY before any async operations
    isPausedRef.current = newPaused;
    console.log(`   ⚡ isPausedRef.current set to: ${isPausedRef.current} (IMMEDIATE)`);
    
    if (newPaused) {
      // Pausing - stop processing IMMEDIATELY
      console.log('⏸️ [Speed Control] Pausing - stopping all processing IMMEDIATELY');
      console.log(`   Clearing ${timeoutIdsRef.current.length} pending setTimeout calls`);
      
      // Clear all pending setTimeout calls IMMEDIATELY
      timeoutIdsRef.current.forEach(id => {
        clearTimeout(id);
      });
      timeoutIdsRef.current = [];
      
      // Clear interval if running
      if (intervalRef.current) {
        console.log('   Clearing interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Stop processing IMMEDIATELY
      processingRef.current = false;
      console.log(`   ✅ After pause: processing=${processingRef.current}, isPausedRef=${isPausedRef.current}, pendingTimeouts=${timeoutIdsRef.current.length}`);
    } else {
      // Resuming - wait for backend confirmation before processing
      // The handleBackendPauseConfirmation callback will start processing
      console.log('▶️ [Speed Control] Resume requested - waiting for backend confirmation');
      console.log(`   Queue length: ${updateQueueRef.current.length}`);
      // Don't start processing here - wait for backend confirmation
      // If backend didn't receive the message, start processing anyway
      if (!sentToBackend) {
        console.log('▶️ [Speed Control] No backend connection - starting processing locally');
        startProcessing();
      }
    }
    
    // Update state (async, but ref is already updated)
    setIsPaused(newPaused);
  }, [isPaused, startProcessing, sendPauseToBackend, socket, gameId]);
  
  /**
   * Clear the queue (useful when disconnecting or changing games)
   */
  const clearQueue = useCallback(() => {
    updateQueueRef.current = [];
    
    // Clear all pending timeouts
    timeoutIdsRef.current.forEach(id => {
      clearTimeout(id);
    });
    timeoutIdsRef.current = [];
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    processingRef.current = false;
  }, []);
  
  // Cleanup on unmount or when disabled
  useEffect(() => {
    if (!enabled) {
      // Speed control disabled - clear queue and process any remaining immediately
      processAllQueued();
      clearQueue();
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      clearQueue();
    };
  }, [enabled, processAllQueued, clearQueue]);
  
  // Sync ref with state
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  
  // Restart processing when speed changes (only if not paused)
  useEffect(() => {
    if (enabled && !isPausedRef.current && updateQueueRef.current.length > 0) {
      startProcessing();
    }
  }, [speed, enabled, startProcessing]);
  
  // Calculate animation speed multiplier (1% = 100x slower, 100% = normal speed)
  const animationSpeedMultiplier = useMemo(() => {
    if (speed >= 100) return 1; // Normal speed
    return speed / 100; // At 1%, multiplier is 0.01 (100x slower)
  }, [speed]);
  
  return {
    speed,
    isPaused,
    backendPaused, // Track if backend has confirmed pause
    queueUpdate,
    updateSpeed,
    togglePause,
    clearQueue,
    handleBackendPauseConfirmation, // Call this when backend confirms pause/resume
    queueLength: updateQueueRef.current.length,
    animationSpeedMultiplier // For slowing down animations
  };
}
